/**
 * My CRM — closed/funded clients for the logged-in MLO.
 *
 * Data is server-scoped to the caller's NMLS (an MLO only ever sees their own
 * clients). Admins can toggle an org-wide view. Clicking a client's email
 * opens a compose window whose subject is pre-filled with
 * "Mana Home Loans LLC - <MLO full name>" and sends via the MLO's configured
 * Gmail SMTP.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import { api, ApiError } from '../lib/api.js';
import { useAuth, useModulePermission } from '../lib/auth-context.js';
import { Button } from '../ui/Button.js';
import { Field, Input } from '../ui/Input.js';
import { Badge, Card, CardBody, CardHeader } from '../ui/Card.js';

interface CrmClient {
  id: string;
  loanNumber: string;
  loanStatus: string | null;
  borrowerName: string | null;
  email: string | null;
  phone: string | null;
  loanRate: number | null;
  term: string | null;
  fico: number | null;
  loanAmount: number | null;
  appraisedValue: number | null;
  epoDate: string | null;
}

interface CrmResponse {
  clients: CrmClient[];
  total: number;
  scope: 'my' | 'all';
  hasNmls: boolean;
  nmls: string | null;
}

interface TodaysRateRow {
  termYears: number;
  fixed: number | null;
  bp10: number | null;
  bp21: number | null;
}
type RateState =
  | { status: 'loading' }
  | { status: 'done'; rows: TodaysRateRow[] }
  | { status: 'error'; reason: string };

// ---------------------------------------------------------------------------
// Column config — drives the header, per-column filters, sort, and cells so
// every column gets consistent filter + sort behavior.
// ---------------------------------------------------------------------------

type ColId =
  | 'borrower'
  | 'email'
  | 'phone'
  | 'rate'
  | 'term'
  | 'loanAmount'
  | 'apprValue'
  | 'fico'
  | 'epo'
  | 'status';

interface CrmColumn {
  id: ColId;
  label: string;
  /** Drives sort comparison + null handling. */
  type: 'string' | 'number' | 'date';
  /** Value used for sorting (null sorts last). */
  sortValue: (c: CrmClient) => number | string | null;
  /** Text matched by the per-column filter (case-insensitive contains). */
  filterText: (c: CrmClient) => string;
  /** Cell renderer. */
  cell: (c: CrmClient, onEmail: (c: CrmClient) => void) => ReactNode;
}

const dash = <span className="text-slate-400">—</span>;
const money = (n: number | null) => (n != null ? `$${Math.round(n).toLocaleString('en-US')}` : null);

const CRM_COLUMNS: CrmColumn[] = [
  {
    id: 'borrower',
    label: 'Borrower',
    type: 'string',
    sortValue: (c) => c.borrowerName ?? null,
    filterText: (c) => `${c.borrowerName ?? ''} ${c.loanNumber}`,
    cell: (c) => (
      <div>
        <div className="font-medium text-brand-navy-800">{c.borrowerName ?? '—'}</div>
        <div className="font-mono text-[11px] text-slate-400">#{c.loanNumber}</div>
      </div>
    ),
  },
  {
    id: 'email',
    label: 'Email',
    type: 'string',
    sortValue: (c) => c.email ?? null,
    filterText: (c) => c.email ?? '',
    cell: (c, onEmail) =>
      c.email ? (
        <button
          type="button"
          onClick={() => onEmail(c)}
          title={`Email ${c.email}`}
          className="block max-w-[180px] truncate text-left text-brand-navy-700 hover:underline"
        >
          {c.email}
        </button>
      ) : (
        dash
      ),
  },
  {
    id: 'phone',
    label: 'Phone',
    type: 'string',
    sortValue: (c) => c.phone ?? null,
    filterText: (c) => c.phone ?? '',
    cell: (c) => <span className="text-slate-700">{c.phone ?? dash}</span>,
  },
  {
    id: 'rate',
    label: 'Rate',
    type: 'number',
    sortValue: (c) => c.loanRate,
    filterText: (c) => (c.loanRate != null ? `${c.loanRate}%` : ''),
    cell: (c) => <span className="text-slate-700">{c.loanRate != null ? `${c.loanRate}%` : '—'}</span>,
  },
  {
    id: 'term',
    label: 'Term',
    type: 'number',
    sortValue: (c) => (c.term ? Number.parseFloat(c.term) || null : null),
    filterText: (c) => (c.term ? `${c.term} yr` : ''),
    cell: (c) => <span className="text-slate-700">{c.term ? `${c.term} yr` : '—'}</span>,
  },
  {
    id: 'loanAmount',
    label: 'Loan Amount',
    type: 'number',
    sortValue: (c) => c.loanAmount,
    filterText: (c) => (c.loanAmount != null ? money(c.loanAmount) ?? '' : ''),
    cell: (c) => <span className="text-slate-700">{money(c.loanAmount) ?? dash}</span>,
  },
  {
    id: 'apprValue',
    label: 'Appraised Value',
    type: 'number',
    sortValue: (c) => c.appraisedValue,
    filterText: (c) => (c.appraisedValue != null ? money(c.appraisedValue) ?? '' : ''),
    cell: (c) => <span className="text-slate-700">{money(c.appraisedValue) ?? dash}</span>,
  },
  {
    id: 'fico',
    label: 'FICO',
    type: 'number',
    sortValue: (c) => c.fico,
    filterText: (c) => (c.fico != null ? String(c.fico) : ''),
    cell: (c) => <span className="text-slate-700">{c.fico ?? '—'}</span>,
  },
  {
    id: 'epo',
    label: 'EPO date',
    type: 'date',
    sortValue: (c) => (c.epoDate ? Date.parse(c.epoDate) : null),
    filterText: (c) => (c.epoDate ? new Date(c.epoDate).toLocaleDateString() : ''),
    cell: (c) => (
      <span className="text-slate-700">
        {c.epoDate ? new Date(c.epoDate).toLocaleDateString() : '—'}
      </span>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    type: 'string',
    sortValue: (c) => c.loanStatus ?? null,
    filterText: (c) => c.loanStatus ?? '',
    cell: (c) => (c.loanStatus ? <Badge tone="neutral">{c.loanStatus}</Badge> : dash),
  },
];

export function CrmPage() {
  const perm = useModulePermission('crm');
  const { me } = useAuth();
  const isAdmin = me?.user.role === 'admin' || me?.user.role === 'super_admin';
  const mloName = me?.user.name ?? '';
  const [scope, setScope] = useState<'my' | 'all'>('my');
  const [q, setQ] = useState('');
  const [compose, setCompose] = useState<CrmClient | null>(null);
  // Client-side sort + per-column filters over the fetched client set.
  const [sortKey, setSortKey] = useState<ColId>('epo');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [filters, setFilters] = useState<Partial<Record<ColId, string>>>({});
  // Live "Today's Rate" results, keyed by loan id (fetched on demand per row).
  const [rates, setRates] = useState<Record<string, RateState>>({});
  const [bulkRunning, setBulkRunning] = useState(false);
  // UWM Control-Your-Price incentive (basis points) applied to every Today's Rate.
  // The MLO saves it explicitly (Save button); it's retained across reloads and
  // applied to every client's rate calc.
  const [crmCypBps, setCrmCypBps] = useState<number>(() => {
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem('crmCypBps') : null;
    const n = saved == null ? 0 : Number(saved);
    return Number.isFinite(n) ? n : 0;
  });
  const [cypMsg, setCypMsg] = useState<string | null>(null);
  const saveCyp = () => {
    try {
      window.localStorage.setItem('crmCypBps', String(crmCypBps));
      setCypMsg('Saved ✓');
    } catch {
      setCypMsg('Save failed');
    }
  };

  const query = useQuery<CrmResponse>({
    queryKey: ['crm-clients', scope, q],
    queryFn: () =>
      api<CrmResponse>(`/api/crm/clients?scope=${scope}${q ? `&q=${encodeURIComponent(q)}` : ''}`),
    enabled: perm.canView,
  });

  // Distinct statuses present (for the Status filter dropdown).
  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of query.data?.clients ?? []) if (c.loanStatus) set.add(c.loanStatus);
    return [...set].sort();
  }, [query.data]);

  // Filter then sort, all client-side.
  const rows = useMemo(() => {
    let list = [...(query.data?.clients ?? [])];
    for (const col of CRM_COLUMNS) {
      const f = filters[col.id]?.trim().toLowerCase();
      if (!f) continue;
      if (col.id === 'status') {
        list = list.filter((c) => (c.loanStatus ?? '') === filters.status);
      } else {
        list = list.filter((c) => col.filterText(c).toLowerCase().includes(f));
      }
    }
    const col = CRM_COLUMNS.find((c) => c.id === sortKey);
    if (col) {
      list.sort((a, b) => {
        const av = col.sortValue(a);
        const bv = col.sortValue(b);
        const an = av === null || av === undefined;
        const bn = bv === null || bv === undefined;
        if (an && bn) return 0;
        if (an) return 1; // nulls always sort last
        if (bn) return -1;
        const base =
          col.type === 'string'
            ? String(av).localeCompare(String(bv))
            : (av as number) - (bv as number);
        return sortDir === 'asc' ? base : -base;
      });
    }
    return list;
  }, [query.data, filters, sortKey, sortDir]);

  const toggleSort = (id: ColId) => {
    if (sortKey === id) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(id);
      setSortDir('asc');
    }
  };
  const anyFilter = Object.values(filters).some((v) => v && v.trim());

  // Fetch today's live ★ rates for one client.
  const fetchRate = async (loanId: string) => {
    setRates((r) => ({ ...r, [loanId]: { status: 'loading' } }));
    try {
      const resp = await api<{ ok: boolean; rows?: TodaysRateRow[]; reason?: string }>(
        `/api/crm/clients/${loanId}/todays-rate`,
        { method: 'POST', body: JSON.stringify({ cypBps: crmCypBps }) },
      );
      setRates((r) => ({
        ...r,
        [loanId]:
          resp.ok && resp.rows
            ? { status: 'done', rows: resp.rows }
            : { status: 'error', reason: resp.reason ?? 'unavailable' },
      }));
    } catch (e) {
      setRates((r) => ({
        ...r,
        [loanId]: { status: 'error', reason: e instanceof ApiError ? 'quote_failed' : 'error' },
      }));
    }
  };

  // Fetch every visible row, max 4 quotes in flight (UWM courtesy + responsiveness).
  const fetchAllRates = async () => {
    setBulkRunning(true);
    const ids = rows.map((c) => c.id);
    let i = 0;
    const worker = async () => {
      while (i < ids.length) {
        const id = ids[i++];
        if (id) await fetchRate(id);
      }
    };
    await Promise.all(Array.from({ length: Math.min(4, ids.length) }, () => worker()));
    setBulkRunning(false);
  };

  if (!perm.canView) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        You don&apos;t have access to My CRM. Ask an administrator to grant the
        CRM module permission.
      </div>
    );
  }

  const data = query.data;
  const showNmlsBanner = scope === 'my' && data && !data.hasNmls;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-gold-700">
            My CRM
          </p>
          <h1 className="mt-1 font-display text-3xl font-bold text-brand-navy-800">My CRM</h1>
          <p className="mt-1 text-sm text-slate-500">
            Your funded &amp; post-closed clients, synced from UWM Insights. Click an email to
            reach out.
          </p>
        </div>
      </header>

      {/* Scope tabs */}
      <div className="flex items-center justify-between border-b border-slate-200">
        <div className="flex gap-1">
          <ScopeTab active={scope === 'my'} onClick={() => setScope('my')}>
            My clients
            {scope === 'my' && data && (
              <span className="ml-2 rounded-full bg-brand-navy-100 px-1.5 py-0.5 text-[10px] font-semibold text-brand-navy-700">
                {data.total.toLocaleString()}
              </span>
            )}
          </ScopeTab>
          {isAdmin && (
            <ScopeTab active={scope === 'all'} onClick={() => setScope('all')}>
              All clients
              <span className="ml-1.5 rounded-full bg-brand-gold-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-gold-700">
                Admin
              </span>
              {scope === 'all' && data && (
                <span className="ml-2 rounded-full bg-brand-navy-100 px-1.5 py-0.5 text-[10px] font-semibold text-brand-navy-700">
                  {data.total.toLocaleString()}
                </span>
              )}
            </ScopeTab>
          )}
        </div>
        {scope === 'my' && me?.user.nmls && (
          <span className="pb-2 text-[11px] text-slate-500">
            Your clients only · NMLS{' '}
            <strong className="font-mono text-brand-navy-700">{me.user.nmls}</strong>
          </span>
        )}
      </div>

      {showNmlsBanner && (
        <div className="rounded-xl border-l-4 border-brand-gold-500 bg-brand-gold-50/40 px-5 py-4 text-sm">
          <div className="font-semibold text-brand-navy-800">Set your NMLS to see your clients</div>
          <p className="mt-0.5 text-xs text-slate-600">
            My CRM filters by your NMLS number. An administrator can set it for you on the
            Administration page.
          </p>
        </div>
      )}

      <Card>
        <CardHeader
          title={`Clients (${rows.length}${
            data && rows.length !== data.total ? ` of ${data.total}` : ''
          })`}
          subtitle="Funded and post-closed loans. Click a column to sort; use the row below to filter."
          actions={
            <div className="flex items-center gap-2">
              {anyFilter && (
                <button
                  type="button"
                  onClick={() => setFilters({})}
                  className="text-[11px] font-medium text-brand-gold-700 hover:underline"
                >
                  Clear filters
                </button>
              )}
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, email, loan #…"
                className="w-56 rounded-md border border-slate-300 px-2 py-1 text-xs shadow-sm focus:border-brand-navy-400 focus:outline-none"
              />
              <label
                className="flex items-center gap-1 text-[11px] text-slate-600"
                title="UWM Control-Your-Price incentive (basis points) applied to Today's Rate"
              >
                CYP
                <input
                  type="number"
                  value={crmCypBps}
                  onChange={(e) => {
                    setCrmCypBps(Math.round(Number(e.target.value) || 0));
                    setCypMsg(null);
                  }}
                  className="w-14 rounded border border-slate-300 px-1.5 py-1 text-slate-800 shadow-sm focus:border-brand-navy-400 focus:outline-none"
                />
                bps
              </label>
              <Button
                size="sm"
                variant="secondary"
                onClick={saveCyp}
                title="Save this CYP — applied to every client's Today's Rate and kept across reloads"
              >
                Save CYP
              </Button>
              {cypMsg && <span className="text-[11px] text-emerald-600">{cypMsg}</span>}
              <Button
                size="sm"
                variant="secondary"
                disabled={bulkRunning || rows.length === 0}
                onClick={fetchAllRates}
                title="Pull today's live rates for every client in the list"
              >
                {bulkRunning ? 'Getting rates…' : 'Get all rates'}
              </Button>
            </div>
          }
        />
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              {/* Sort row */}
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {CRM_COLUMNS.map((col) => {
                  const active = sortKey === col.id;
                  return (
                    <th key={col.id} className="px-2 py-2.5">
                      <button
                        type="button"
                        onClick={() => toggleSort(col.id)}
                        className="group inline-flex items-center gap-1 hover:text-brand-navy-700"
                        title={`Sort by ${col.label}`}
                      >
                        {col.label}
                        <span
                          className={clsx(
                            'text-[10px]',
                            active ? 'text-brand-navy-700' : 'text-slate-300 group-hover:text-slate-400',
                          )}
                        >
                          {active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    </th>
                  );
                })}
                <th className="px-2 py-2.5 text-left">
                  <div className="inline-flex flex-col">
                    <span>Today&apos;s Rate</span>
                    <span className="text-[9px] font-normal normal-case tracking-normal text-slate-400">
                      ★ note rate
                    </span>
                  </div>
                </th>
              </tr>
              {/* Filter row */}
              <tr className="border-b border-slate-100 bg-white">
                {CRM_COLUMNS.map((col) => (
                  <th key={col.id} className="px-3 pb-2 pt-1 font-normal">
                    {col.id === 'status' ? (
                      <select
                        value={filters.status ?? ''}
                        onChange={(e) =>
                          setFilters((f) => ({ ...f, status: e.target.value }))
                        }
                        className="w-full rounded border border-slate-200 px-1.5 py-1 text-[11px] font-normal text-slate-600 focus:border-brand-navy-400 focus:outline-none"
                      >
                        <option value="">All</option>
                        {statusOptions.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={filters[col.id] ?? ''}
                        onChange={(e) =>
                          setFilters((f) => ({ ...f, [col.id]: e.target.value }))
                        }
                        placeholder="Filter…"
                        className="w-full rounded border border-slate-200 px-1.5 py-1 text-[11px] font-normal text-slate-700 placeholder:text-slate-300 focus:border-brand-navy-400 focus:outline-none"
                      />
                    )}
                  </th>
                ))}
                <th className="px-3 pb-2 pt-1" />
              </tr>
            </thead>
            <tbody>
              {query.isLoading && (
                <tr>
                  <td colSpan={CRM_COLUMNS.length + 1} className="px-4 py-6 text-center text-sm text-slate-500">
                    Loading clients…
                  </td>
                </tr>
              )}
              {!query.isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={CRM_COLUMNS.length + 1} className="px-4 py-6 text-center text-sm text-slate-500">
                    {data && data.total > 0
                      ? 'No clients match the current filters.'
                      : `No funded or post-closed clients${scope === 'my' ? ' under your NMLS' : ''} yet.`}
                  </td>
                </tr>
              )}
              {rows.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-brand-navy-50/40">
                  {CRM_COLUMNS.map((col) => (
                    <td key={col.id} className="px-2 py-2.5 align-top">
                      {col.cell(c, setCompose)}
                    </td>
                  ))}
                  <td className="px-2 py-2.5 align-top">
                    <RateCell state={rates[c.id]} onGet={() => fetchRate(c.id)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {compose && (
        <CrmComposeModal
          client={compose}
          mloName={mloName}
          onClose={() => setCompose(null)}
        />
      )}
    </div>
  );
}

function ScopeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex items-center gap-1 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'border-brand-navy-700 text-brand-navy-800'
          : 'border-transparent text-slate-500 hover:text-brand-navy-700',
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Today's Rate cell — a per-row "Get rate" button that, once fetched, shows a
// grid of the best ★ note rate for each term (30/20/15yr) × program (fixed,
// 1-0 buydown, 2-1 buydown). "--" = no rate met the criteria for that cell.
// ---------------------------------------------------------------------------

const fmtRate = (n: number | null) => (n == null ? '--' : `${n.toFixed(3)}%`);

function RateCell({ state, onGet }: { state: RateState | undefined; onGet: () => void }) {
  if (!state) {
    return (
      <button
        type="button"
        onClick={onGet}
        className="rounded-md border border-brand-navy-200 px-2 py-1 text-[11px] font-medium text-brand-navy-700 hover:bg-brand-navy-50"
      >
        Get rate
      </button>
    );
  }
  if (state.status === 'loading') return <span className="text-xs text-slate-400">Pricing…</span>;
  if (state.status === 'error') {
    const msg =
      state.reason === 'insufficient_data'
        ? 'No loan data'
        : state.reason === 'quote_failed'
          ? 'Quote failed'
          : 'Error';
    return (
      <div className="text-[11px]">
        <span
          className="text-rose-500"
          title={
            state.reason === 'insufficient_data'
              ? 'This loan has no stored amount/value yet — run a full UWM sync to backfill it.'
              : ''
          }
        >
          {msg}
        </span>{' '}
        <button type="button" onClick={onGet} className="text-brand-navy-600 hover:underline">
          retry
        </button>
      </div>
    );
  }
  // Grid: term (rows) × program (Fixed / 1-0 / 2-1 buydown), showing the ★ note rate.
  return (
    <div className="whitespace-nowrap text-[11px] leading-tight">
      <table className="border-collapse">
        <thead>
          <tr className="text-[9px] uppercase tracking-wide text-slate-400">
            <th className="pb-0.5 pr-2 text-left font-medium"> </th>
            <th className="px-1 pb-0.5 text-right font-medium">Fixed</th>
            <th className="px-1 pb-0.5 text-right font-medium">1-0</th>
            <th className="px-1 pb-0.5 text-right font-medium">2-1</th>
          </tr>
        </thead>
        <tbody>
          {state.rows.map((r) => (
            <tr key={r.termYears}>
              <td className="pr-1.5 text-slate-400">{r.termYears}y</td>
              <td className="px-1 text-right font-medium text-brand-navy-800">{fmtRate(r.fixed)}</td>
              <td className="px-1 text-right font-medium text-brand-navy-800">{fmtRate(r.bp10)}</td>
              <td className="px-1 text-right font-medium text-brand-navy-800">{fmtRate(r.bp21)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-1 flex items-center justify-between gap-3 text-[9px] text-slate-400">
        <span>1-0 · 2-1 = buydown note rate</span>
        <button type="button" onClick={onGet} title="Refresh" className="hover:text-brand-navy-600">
          ↻ refresh
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compose modal — subject prefilled "Mana Home Loans LLC - <MLO full name>".
// Sends via the MLO's Gmail SMTP (POST /api/crm/clients/:id/email), which
// re-validates the client is within the MLO's NMLS scope.
// ---------------------------------------------------------------------------

function CrmComposeModal({
  client,
  mloName,
  onClose,
}: {
  client: CrmClient;
  mloName: string;
  onClose: () => void;
}) {
  const [subject, setSubject] = useState(
    `Mana Home Loans LLC - ${mloName}`.trim(),
  );
  const [body, setBody] = useState('');
  const [result, setResult] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  const send = useMutation({
    mutationFn: () =>
      api(`/api/crm/clients/${client.id}/email`, {
        method: 'POST',
        body: JSON.stringify({ to: client.email, subject, text: body || ' ' }),
      }),
    onSuccess: () => setResult({ tone: 'ok', text: 'Email sent.' }),
    onError: (e: Error) =>
      setResult({
        tone: 'err',
        text: e instanceof ApiError ? (e.message ?? 'Send failed') : e.message,
      }),
  });

  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={onClose} aria-hidden />
      <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-5 shadow-floating">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-gold-700">
              New email
            </p>
            <h3 className="font-display text-lg font-bold text-brand-navy-800">
              To {client.borrowerName ?? client.email}
            </h3>
            <p className="text-xs text-slate-500">{client.email}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            ✕
          </Button>
        </div>
        <p className="mt-1 text-[11px] text-slate-500">
          Sends through your configured Gmail SMTP (Integrations tab) and is logged on the loan.
        </p>
        <div className="mt-3 space-y-3">
          <Field label="Subject">
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Field>
          <Field label="Message">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              placeholder="Write your message…"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
            />
          </Field>
          {result && (
            <div
              className={clsx(
                'rounded-lg px-3 py-2 text-xs ring-1',
                result.tone === 'ok'
                  ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                  : 'bg-rose-50 text-rose-700 ring-rose-200',
              )}
            >
              {result.text}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              {result?.tone === 'ok' ? 'Close' : 'Cancel'}
            </Button>
            <Button
              disabled={send.isPending || !subject.trim() || result?.tone === 'ok'}
              onClick={() => {
                setResult(null);
                send.mutate();
              }}
            >
              {send.isPending ? 'Sending…' : 'Send email'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
